import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { AuthService } from '../services/auth'
import { HomeworkService, type Homework, type HomeworkSubmission } from '../services/homework'
import submissionIconMark from '../assets/icon/submission-icon-mark.svg'

const HomeworkCatalogPage = () => {
  const navigate = useNavigate()
  const [currentUser, setCurrentUser] = useState<{ id: string } | null>(null)
  const [homeworkList, setHomeworkList] = useState<Homework[]>([])
  const [submissions, setSubmissions] = useState<HomeworkSubmission[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    const init = async () => {
      const user = await AuthService.getCurrentUser()
      if (!user) {
        navigate('/login')
        return
      }
      setCurrentUser(user)
      try {
        const [hw, subs] = await Promise.all([
          HomeworkService.getSystemHomework(),
          HomeworkService.getMySubmissions(user.id)
        ])
        setHomeworkList(hw)
        setSubmissions(subs)
      } catch (err) {
        console.error(err)
        setError('宿題の読み込みに失敗しました')
      } finally {
        setIsLoading(false)
      }
    }
    init()
  }, [navigate])

  const getSubmission = (homeworkId: string) =>
    submissions.find(s => s.homework_id === homeworkId) || null

  const handleSelect = async (homeworkId: string) => {
    if (!currentUser) return
    await HomeworkService.upsertSystemHomeworkSelection(currentUser.id, homeworkId)
    navigate(`/homework/${homeworkId}`)
  }

  // Group by category
  const byCategory = homeworkList.reduce<Record<string, Homework[]>>((acc, hw) => {
    const cat = hw.category || 'その他'
    if (!acc[cat]) acc[cat] = []
    acc[cat].push(hw)
    return acc
  }, {})

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-24">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#65c1c8]" />
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 pt-4 pb-8">
      {/* 現在の宿題 と同じアイコン＋白字の見出し（レインボーの上に載る） */}
      <div className="flex items-center justify-between gap-3 mb-6">
        <h1 className="flex items-center gap-2">
          <img alt="" className="h-10 w-[45px] shrink-0" src={submissionIconMark} />
          <span className="text-xl font-bold text-white">ミラパスからの宿題を設定</span>
        </h1>
        <Link to="/homework" className="text-sm font-semibold text-white hover:opacity-80 whitespace-nowrap">← 宿題に戻る</Link>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      {homeworkList.length === 0 && !error && (
        <div className="text-center py-16 text-gray-500">
          <p className="text-lg">現在、利用できるシステム宿題はありません</p>
        </div>
      )}

      <div className="space-y-8">
        {Object.entries(byCategory).map(([category, items]) => (
          <section key={category}>
            <h2 className="text-base font-semibold text-gray-700 mb-3 pb-2 border-b border-gray-200">
              {category}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map(hw => {
                const sub = getSubmission(hw.id)
                return (
                  <div
                    key={hw.id}
                    className="bg-white rounded-xl shadow p-5 flex flex-col"
                  >
                    <h3 className="font-semibold text-gray-900 mb-2">{hw.title}</h3>
                    {hw.description && (
                      <p className="text-sm text-gray-600 mb-4 flex-1 line-clamp-3">{hw.description}</p>
                    )}
                    <div className="flex items-center justify-between mt-auto pt-3 border-t border-gray-100">
                      {sub ? (
                        <Link
                          to={`/homework/${hw.id}`}
                          className="inline-flex items-center gap-1 text-sm font-semibold text-green-700 hover:underline"
                        >
                          <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />
                          提出済み — 結果を見る
                        </Link>
                      ) : (
                        <button
                          onClick={() => handleSelect(hw.id)}
                          className="inline-flex items-center px-4 py-2 bg-[#65c1c8] text-white text-sm font-semibold rounded-lg hover:bg-[#5ab0b7] transition-colors"
                        >
                          この宿題をやる
                        </button>
                      )}
                      {hw.related_video_id && (
                        <Link
                          to={`/video/${hw.related_video_id}`}
                          className="text-xs text-[#da4574] hover:underline"
                        >
                          関連動画
                        </Link>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </section>
        ))}
      </div>

    </div>
  )
}

export default HomeworkCatalogPage
